import type { PMTilesLayer } from '~/hooks/usePMTiles'
import { createLinearBreaks } from '~/util'
import { createColorBreaks } from '~/util/color'
import {
  COLORMAP_YELLOW,
  COLORMAP_DIVERGING_RWB,
  COLORMAP_DIVERGING_RWB_ENHANCED,
} from '~/constants'

// Standard diverging metrics: simple 3-color scale, normal radius
const DIVERGING_METRICS = [
  'Hsmean_mean_percentage_change',
  'Hs_q99_mean_percentage_change',
  'WPmean_mean_percentage_change',
]

// Enhanced diverging metrics: 5-color scale with faster transitions, 2x radius
const DIVERGING_METRICS_ENHANCED = ['hs_rp_100_mean_percentage_change']

// Pattern for projected Hs change metrics (ratio_rp_*_percentage_change)
// These are dynamically generated based on user-selected return period and year
const PROJECTED_HS_CHANGE_PATTERN = /^ratio_rp_\d+_\d{4}_2025_.*_percentage_change$/

/** Check if a metric should use diverging (centered on zero) styling */
export function isDivergingMetric(attribute: string): boolean {
  return DIVERGING_METRICS.includes(attribute) || isEnhancedDivergingMetric(attribute)
}

/** Check if a metric should use enhanced diverging styling (faster colors, larger radius) */
export function isEnhancedDivergingMetric(attribute: string): boolean {
  return (
    DIVERGING_METRICS_ENHANCED.includes(attribute) || PROJECTED_HS_CHANGE_PATTERN.test(attribute)
  )
}

/**
 * Creates breaks for a diverging scale centered on zero.
 */
function createDivergingBreaks(bounds: { min: number; max: number }): number[] {
  const { min, max } = bounds

  // Use the larger absolute value to create a symmetric scale around zero
  const absMax = Math.min(50, Math.max(Math.abs(min), Math.abs(max)))

  return [-absMax, -absMax / 2, 0, absMax / 2, absMax]
}

/*
 * This function dynamically scales the color breaks for a given
 * attribute/depth combo based on the bounds of the input. In the future we may
 * want to change how this works for absolute attributes (like total power) vs.
 * relative attributes (% change).
 */
export function colorBreaksForAttributeAndDepths(
  depths: number[],
  attribute: string,
  layers: PMTilesLayer[]
) {
  const bounds = boundsForAttributeAndDepths(depths, attribute, layers)
  if (!bounds) {
    return []
  }

  // Use enhanced diverging colormap (5 colors, faster transitions)
  if (isEnhancedDivergingMetric(attribute)) {
    const breaks = createDivergingBreaks(bounds)
    return createColorBreaks(breaks, COLORMAP_DIVERGING_RWB_ENHANCED).sort((a, b) => b[0] - a[0])
  }

  // Use standard diverging colormap (3 colors)
  if (DIVERGING_METRICS.includes(attribute)) {
    const breaks = createDivergingBreaks(bounds)
    return createColorBreaks(breaks, COLORMAP_DIVERGING_RWB).sort((a, b) => b[0] - a[0])
  }

  // Default: linear breaks with yellow colormap
  const breaks = createLinearBreaks(bounds, 4)
  return createColorBreaks(breaks, COLORMAP_YELLOW).sort((a, b) => b[0] - a[0])
}

export function boundsForAttributeAndDepths(
  depths: number[],
  attribute: string,
  layers: PMTilesLayer[]
): { min: number; max: number } | null {
  const depth_strings = depths.map((d) => `depth_${d}`)
  const visibleLayers = layers?.filter((l) => depth_strings.includes(l.layer)) ?? []

  const firstLayerAttribute = visibleLayers[0]?.attributes.find((a) => a.attribute === attribute)!
  // If there's no layers or this is not a findable attribute for some reason
  // then we have no way to calculate bounds.
  if (!firstLayerAttribute) {
    return null
  }

  const bounds = { min: firstLayerAttribute.min!, max: firstLayerAttribute.max! }
  visibleLayers.forEach((l) => {
    const a = l.attributes.find((a) => a.attribute === attribute)!
    if (a.min! < bounds.min!) {
      bounds.min = a.min!
    }
    if (a.max! > bounds.max!) {
      bounds.max = a.max!
    }
  })
  return bounds
}
